// @ts-nocheck — conflicto de tipos entre @types/testing-library__react y @types/react
import React, { useCallback, useEffect, useState } from 'react'
import {
    Button,
    Descriptions,
    Divider,
    Drawer,
    Empty,
    Form,
    Image,
    Input,
    Popconfirm,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd'
import {
    CheckCircleFilled,
    CloseCircleFilled,
    PictureOutlined,
    ReloadOutlined,
    SettingOutlined,
    ShoppingOutlined,
    SyncOutlined,
} from '@ant-design/icons'

import axios from 'axios'
import { serverUrl } from '../../Constants/env'
import { useSelector } from 'react-redux'

const { Text } = Typography

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface StockRow {
    qty: number
    is_in_stock: boolean
    importado_at: string | null
}

interface MediaRow {
    id: number
    url: string
    label: string | null
    posicion: number
    es_principal: boolean
}

interface CategoriaRow {
    id: number
    magento_id: number
    nombre: string
    nivel: number
}

interface ProductoRow {
    id: number
    pais: string
    sku: string
    magento_id: number | null
    nombre: string
    tipo: 'simple' | 'configurable' | 'bundle'
    estado: number
    visibilidad: number
    precio: number | null
    precio_especial: number | null
    descripcion: string | null
    descripcion_corta: string | null
    peso: number | null
    atributos_json: Record<string, any> | null
    importado_at: string | null
    stock?: StockRow | null
    medias?: MediaRow[]
    categorias?: CategoriaRow[]
}

interface CategoriaOption {
    id: number
    nombre: string
    nivel: number
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const PAISES = [
    { value: 'cl', label: '🇨🇱 Chile' },
    { value: 'ar', label: '🇦🇷 Argentina' },
]

const TIPOS: Record<string, { color: string; label: string }> = {
    simple:       { color: 'blue',   label: 'Simple' },
    configurable: { color: 'purple', label: 'Configurable' },
    bundle:       { color: 'orange', label: 'Bundle' },
}

const formatPrice = (value: number | null) =>
    value != null
        ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value)
        : '—'

// ─── Drawer de detalle ────────────────────────────────────────────────────────

const ProductoDrawer: React.FC<{
    producto: ProductoRow | null
    visible: boolean
    onClose: () => void
}> = ({ producto, visible, onClose }) => {
    if (!producto) return null

    const tipo = TIPOS[producto.tipo] ?? { color: 'default', label: producto.tipo }

    return (
        <Drawer
            title={
                <Space>
                    <ShoppingOutlined />
                    <span>{producto.sku}</span>
                    <Tag color={tipo.color}>{tipo.label}</Tag>
                </Space>
            }
            width={680}
            open={visible}
            onClose={onClose}
            destroyOnClose
        >
            <Space direction="vertical" size="large" style={{ width: '100%' }}>

                {/* Galería */}
                {producto.medias && producto.medias.length > 0 ? (
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 8 }}>
                            <PictureOutlined /> Galería
                        </div>
                        <Image.PreviewGroup>
                            <Space wrap>
                                {producto.medias.map((m) => (
                                    <Image
                                        key={m.id}
                                        src={m.url}
                                        width={100}
                                        height={100}
                                        style={{ objectFit: 'cover', borderRadius: 4 }}
                                        preview={{ src: m.url }}
                                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                                    />
                                ))}
                            </Space>
                        </Image.PreviewGroup>
                    </div>
                ) : (
                    <Empty description="Sin imágenes importadas" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}

                {/* Datos principales */}
                <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="Nombre" span={2}>
                        {producto.nombre}
                    </Descriptions.Item>
                    <Descriptions.Item label="País">
                        <Tag>{producto.pais.toUpperCase()}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Tipo">
                        <Tag color={tipo.color}>{tipo.label}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Estado">
                        {producto.estado === 1
                            ? <Tag color="success">Habilitado</Tag>
                            : <Tag color="default">Deshabilitado</Tag>
                        }
                    </Descriptions.Item>
                    <Descriptions.Item label="Visibilidad">
                        {{ 1: 'No visible', 2: 'Catálogo', 3: 'Búsqueda', 4: 'Catálogo y búsqueda' }[producto.visibilidad] ?? producto.visibilidad}
                    </Descriptions.Item>
                    <Descriptions.Item label="Precio">
                        <Statistic value={producto.precio ?? 0} prefix="$" groupSeparator="." valueStyle={{ fontSize: '1em' }} />
                    </Descriptions.Item>
                    <Descriptions.Item label="Precio especial">
                        {producto.precio_especial != null
                            ? <Statistic value={producto.precio_especial} prefix="$" groupSeparator="." valueStyle={{ fontSize: '1em', color: '#f5222d' }} />
                            : <span style={{ color: '#ccc' }}>—</span>
                        }
                    </Descriptions.Item>
                    <Descriptions.Item label="Stock" span={2}>
                        {producto.stock
                            ? (
                                <Space>
                                    <strong>{Number(producto.stock.qty).toLocaleString('es-CL')}</strong> unidades
                                    {producto.stock.is_in_stock
                                        ? <Tag color="success">En stock</Tag>
                                        : <Tag color="error">Sin stock</Tag>
                                    }
                                </Space>
                            )
                            : <span style={{ color: '#ccc' }}>Sin datos</span>
                        }
                    </Descriptions.Item>
                    <Descriptions.Item label="Peso">
                        {producto.peso != null ? `${producto.peso} kg` : <span style={{ color: '#ccc' }}>—</span>}
                    </Descriptions.Item>
                    <Descriptions.Item label="ID Magento">
                        {producto.magento_id ?? <span style={{ color: '#ccc' }}>—</span>}
                    </Descriptions.Item>
                    <Descriptions.Item label="Categorías" span={2}>
                        {producto.categorias && producto.categorias.length > 0
                            ? <Space wrap>{producto.categorias.map(c => <Tag key={c.id}>{c.nombre}</Tag>)}</Space>
                            : <span style={{ color: '#ccc' }}>—</span>
                        }
                    </Descriptions.Item>
                    <Descriptions.Item label="Última importación" span={2}>
                        {producto.importado_at ?? <span style={{ color: '#ccc' }}>—</span>}
                    </Descriptions.Item>
                </Descriptions>

                {/* Descripción corta */}
                {producto.descripcion_corta && (
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>Descripción corta</div>
                        <div
                            style={{ fontSize: 13, color: '#555', background: '#fafafa', padding: 12, borderRadius: 4 }}
                            dangerouslySetInnerHTML={{ __html: producto.descripcion_corta }}
                        />
                    </div>
                )}

                {/* Descripción completa */}
                {producto.descripcion && (
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>Descripción</div>
                        <div
                            style={{ fontSize: 13, color: '#555', background: '#fafafa', padding: 12, borderRadius: 4, maxHeight: 300, overflow: 'auto' }}
                            dangerouslySetInnerHTML={{ __html: producto.descripcion }}
                        />
                    </div>
                )}

                {/* Atributos JSON */}
                {(() => {
                    const raw = producto.atributos_json
                    const atributos = typeof raw === 'string' ? (() => { try { return JSON.parse(raw) } catch { return null } })() : raw
                    if (!atributos || typeof atributos !== 'object' || Object.keys(atributos).length === 0) return null
                    return (
                        <div>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>Atributos Magento</div>
                            <Descriptions bordered size="small" column={2}>
                                {Object.entries(atributos).map(([k, v]) => (
                                    <Descriptions.Item key={k} label={k}>
                                        {String(v)}
                                    </Descriptions.Item>
                                ))}
                            </Descriptions>
                        </div>
                    )
                })()}
            </Space>
        </Drawer>
    )
}

// ─── Componente principal ─────────────────────────────────────────────────────

const MagentoIndex = ({ exposeSubMenu, setMainLoading }: any) => {
    const { token } = useSelector((state: any) => state)

    const [pais, setPais] = useState<string>('cl')
    const [productos, setProductos] = useState<ProductoRow[]>([])
    const [categorias, setCategorias] = useState<CategoriaOption[]>([])
    const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 })
    const [loading, setLoading] = useState(false)
    const [syncLoading, setSyncLoading] = useState<Record<string, boolean>>({})
    const [drawerProducto, setDrawerProducto] = useState<ProductoRow | null>(null)
    const [drawerLoading, setDrawerLoading] = useState(false)
    const [accionesVisible, setAccionesVisible] = useState(false)

    const [form] = Form.useForm()
    const [filters, setFilters] = useState<Record<string, any>>({})

    const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    }

    // Cargar categorías al cambiar país
    const fetchCategorias = useCallback(async (p: string) => {
        try {
            const res = await axios.get(`${serverUrl}v3/pim/${p}/categorias`, { headers })
            setCategorias(res.data || [])
        } catch {
            setCategorias([])
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    // Cargar productos con filtros y paginación
    const fetchProductos = useCallback(async (p: string, page = 1, extraFilters = {}) => {
        setLoading(true)
        try {
            const raw: any = { per_page: 50, page, ...extraFilters }
            // Eliminar claves con undefined/null para que no lleguen como "undefined" al backend
            const params = Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== undefined && v !== null))
            const qs = new URLSearchParams(params).toString()
            const res = await axios.get(`${serverUrl}v3/pim/${p}/productos?${qs}`, { headers })
            setProductos(res.data.data || [])
            setPagination({
                current: res.data.current_page,
                pageSize: res.data.per_page,
                total: res.data.total,
            })
        } catch {
            message.error('Error al cargar productos')
        } finally {
            setLoading(false)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    useEffect(() => {
        exposeSubMenu([])
        setMainLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        form.resetFields()
        setFilters({})
        fetchCategorias(pais)
        fetchProductos(pais, 1, {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pais])

    // Abrir detalle de producto
    const handleOpenDrawer = async (sku: string) => {
        setDrawerLoading(true)
        setDrawerProducto(null)
        try {
            const res = await axios.get(`${serverUrl}v3/pim/${pais}/producto/${encodeURIComponent(sku)}`, { headers })
            setDrawerProducto(res.data)
        } catch {
            message.error('Error al cargar detalle del producto')
        } finally {
            setDrawerLoading(false)
        }
    }

    // Trigger de sync
    const handleSync = async (tipo: 'categorias' | 'productos' | 'stock' | 'marcas') => {
        setSyncLoading(prev => ({ ...prev, [tipo]: true }))
        try {
            const endpointMap = {
                categorias: `v3/pim/${pais}/sync/categorias`,
                productos:  `v3/pim/${pais}/sync/productos`,
                stock:      `v3/pim/${pais}/sync/stock`,
                marcas:     `v3/pim/${pais}/sync/marcas`,
            }
            await axios.post(`${serverUrl}${endpointMap[tipo]}`, {}, { headers })
            message.success(`Importación de ${tipo} encolada. El proceso corre en segundo plano.`)
        } catch {
            message.error(`Error al encolar importación de ${tipo}`)
        } finally {
            setSyncLoading(prev => ({ ...prev, [tipo]: false }))
        }
    }

    // Trigger Shopify
    const handleShopify = async (tipo: 'all' | 'stock' | 'collections') => {
        setSyncLoading(prev => ({ ...prev, [`shopify_${tipo}`]: true }))
        try {
            const endpointMap = {
                all:         `v3/shopify/${pais}/push/all`,
                stock:       `v3/shopify/${pais}/sync/stock`,
                collections: `v3/shopify/${pais}/sync/collections`,
            }
            await axios.post(`${serverUrl}${endpointMap[tipo]}`, {}, { headers })
            message.success(`Operación Shopify "${tipo}" encolada. El proceso corre en segundo plano.`)
        } catch {
            message.error(`Error al encolar operación Shopify "${tipo}"`)
        } finally {
            setSyncLoading(prev => ({ ...prev, [`shopify_${tipo}`]: false }))
        }
    }

    const onFilterFinish = (values: any) => {
        const f = {
            search:       values.search || undefined,
            tipo:         values.tipo || undefined,
            estado:       values.estado ?? undefined,
            categoria_id: values.categoria_id || undefined,
        }
        setFilters(f)
        fetchProductos(pais, 1, f)
    }

    const columns = [
        {
            title: '',
            key: 'img',
            width: 56,
            render: (_: any, row: ProductoRow) => (
                <div
                    style={{ width: 40, height: 40, borderRadius: 4, overflow: 'hidden', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                    onClick={() => handleOpenDrawer(row.sku)}
                >
                    {(row as any).imagen_principal
                        ? <img src={(row as any).imagen_principal} alt={row.nombre} style={{ width: 40, height: 40, objectFit: 'cover' }} />
                        : <PictureOutlined style={{ color: '#ccc' }} />
                    }
                </div>
            ),
        },
        {
            title: 'SKU / Nombre',
            key: 'nombre',
            render: (_: any, row: ProductoRow) => (
                <span style={{ lineHeight: '1.3' }}>
                    <div style={{ fontWeight: 600, cursor: 'pointer', color: '#1890ff' }} onClick={() => handleOpenDrawer(row.sku)}>
                        {row.nombre}
                    </div>
                    <small style={{ color: '#999' }}>{row.sku}</small>
                </span>
            ),
        },
        {
            title: 'Tipo',
            dataIndex: 'tipo',
            key: 'tipo',
            width: 120,
            render: (v: string) => {
                const t = TIPOS[v] ?? { color: 'default', label: v }
                return <Tag color={t.color}>{t.label}</Tag>
            },
        },
        {
            title: 'Precio',
            key: 'precio',
            width: 120,
            align: 'right' as const,
            render: (_: any, row: ProductoRow) => (
                <span>
                    {formatPrice(row.precio)}
                    {row.precio_especial != null && (
                        <div style={{ color: '#f5222d', fontSize: 12 }}>
                            {formatPrice(row.precio_especial)}
                        </div>
                    )}
                </span>
            ),
        },
        {
            title: 'Stock',
            key: 'stock',
            width: 100,
            align: 'right' as const,
            render: (_: any, row: ProductoRow) => {
                if (!row.stock) return <span style={{ color: '#ccc' }}>—</span>
                return (
                    <Tooltip title={row.stock.is_in_stock ? 'En stock' : 'Sin stock'}>
                        <span style={{ color: row.stock.is_in_stock ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>
                            {Number(row.stock.qty).toLocaleString('es-CL')}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            title: 'Estado',
            dataIndex: 'estado',
            key: 'estado',
            width: 100,
            render: (v: number) =>
                v === 1
                    ? <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />
                    : <CloseCircleFilled style={{ color: '#d9d9d9', fontSize: 16 }} />,
        },
        {
            title: '',
            key: 'action',
            width: 80,
            render: (_: any, row: ProductoRow) => (
                <Button size="small" ghost type="primary" loading={drawerLoading} onClick={() => handleOpenDrawer(row.sku)}>
                    Ver
                </Button>
            ),
        },
    ]

    return (
        <div style={{ padding: 24 }}>

            {/* Header */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h2 style={{ margin: 0 }}>PIM — Gestión de Productos</h2>
                    <div style={{ color: '#999', fontSize: 13, marginTop: 2 }}>
                        Mantenedor de mantenedor centralizado de productos por país
                    </div>
                </div>

                <Space>
                    <Select
                        value={pais}
                        onChange={setPais}
                        options={PAISES}
                        style={{ width: 140 }}
                    />
                    <Button icon={<ReloadOutlined />} onClick={() => fetchProductos(pais, pagination.current, filters)} loading={loading} />
                    <Button icon={<SettingOutlined />} onClick={() => setAccionesVisible(true)}>
                        Acciones
                    </Button>
                </Space>

                {/* Drawer de acciones */}
                <Drawer
                    title="Acciones PIM"
                    width={360}
                    open={accionesVisible}
                    onClose={() => setAccionesVisible(false)}
                    bodyStyle={{ padding: '16px 24px' }}
                >
                    {/* Sección Magento */}
                    <Divider orientation="left" orientationMargin={0}>
                        <Text strong style={{ fontSize: 13 }}>Magento</Text>
                    </Divider>
                    <Space direction="vertical" style={{ width: '100%' }} size={8}>
                        <div>
                            <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
                                Importa el árbol completo de categorías del país seleccionado.
                            </div>
                            <Popconfirm
                                title={`¿Importar categorías de ${pais.toUpperCase()} desde Magento?`}
                                okText="Importar" cancelText="Cancelar"
                                onConfirm={() => handleSync('categorias')}
                            >
                                <Button block loading={syncLoading['categorias']} icon={<SyncOutlined />}>
                                    Importar categorías
                                </Button>
                            </Popconfirm>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
                                Importa todos los productos, imágenes y categorías. Puede tardar varios minutos.
                            </div>
                            <Popconfirm
                                title={`¿Importar todos los productos de ${pais.toUpperCase()}? Puede tardar varios minutos.`}
                                okText="Importar" cancelText="Cancelar"
                                onConfirm={() => handleSync('productos')}
                            >
                                <Button block loading={syncLoading['productos']} icon={<SyncOutlined />} type="primary">
                                    Importar productos completo
                                </Button>
                            </Popconfirm>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
                                Actualiza solo el stock de todos los productos ya importados.
                            </div>
                            <Popconfirm
                                title={`¿Actualizar stock de todos los productos de ${pais.toUpperCase()}?`}
                                okText="Actualizar" cancelText="Cancelar"
                                onConfirm={() => handleSync('stock')}
                            >
                                <Button block loading={syncLoading['stock']} icon={<SyncOutlined />}>
                                    Actualizar stock
                                </Button>
                            </Popconfirm>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
                                Importa las marcas desde Magento y las asigna a cada producto.
                            </div>
                            <Popconfirm
                                title={`¿Importar marcas desde Magento para ${pais.toUpperCase()}?`}
                                okText="Importar" cancelText="Cancelar"
                                onConfirm={() => handleSync('marcas')}
                            >
                                <Button block loading={syncLoading['marcas']} icon={<SyncOutlined />}>
                                    Importar marcas
                                </Button>
                            </Popconfirm>
                        </div>
                    </Space>

                    {/* Sección Shopify */}
                    <Divider orientation="left" orientationMargin={0} style={{ marginTop: 28 }}>
                        <Text strong style={{ fontSize: 13 }}>Shopify</Text>
                    </Divider>
                    <Space direction="vertical" style={{ width: '100%' }} size={8}>
                        <div>
                            <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
                                Sincroniza las categorías PIM como colecciones en Shopify.
                            </div>
                            <Popconfirm
                                title="¿Sincronizar colecciones hacia Shopify?"
                                onConfirm={() => handleShopify('collections')}
                            >
                                <Button block icon={<SyncOutlined />} loading={syncLoading['shopify_collections']}>
                                    Sincronizar colecciones → Shopify
                                </Button>
                            </Popconfirm>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
                                Envía todos los productos del país seleccionado a la tienda Shopify correspondiente.
                            </div>
                            <Popconfirm
                                title="¿Publicar TODOS los productos en Shopify? Esto puede tomar varios minutos."
                                onConfirm={() => handleShopify('all')}
                            >
                                <Button block icon={<ShoppingOutlined />} type="primary" loading={syncLoading['shopify_all']}>
                                    Publicar todos en Shopify
                                </Button>
                            </Popconfirm>
                        </div>
                        <div>
                            <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
                                Sincroniza solo el stock e inventario hacia Shopify.
                            </div>
                            <Popconfirm
                                title="¿Sincronizar stock hacia Shopify?"
                                onConfirm={() => handleShopify('stock')}
                            >
                                <Button block icon={<SyncOutlined />} loading={syncLoading['shopify_stock']}>
                                    Sincronizar stock → Shopify
                                </Button>
                            </Popconfirm>
                        </div>
                    </Space>
                </Drawer>
            </div>

            {/* Filtros */}
            <Form
                form={form}
                layout="inline"
                onFinish={onFilterFinish}
                style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
            >
                <Form.Item name="search">
                    <Input
                        placeholder="SKU o nombre..."
                        allowClear
                        style={{ width: 220 }}
                        onBlur={() => form.submit()}
                        onKeyDown={e => e.key === 'Enter' && form.submit()}
                    />
                </Form.Item>
                <Form.Item name="tipo">
                    <Select
                        placeholder="Tipo"
                        allowClear
                        style={{ width: 140 }}
                        onChange={() => form.submit()}
                        options={[
                            { value: 'simple',       label: 'Simple' },
                            { value: 'configurable', label: 'Configurable' },
                            { value: 'bundle',       label: 'Bundle' },
                        ]}
                    />
                </Form.Item>
                <Form.Item name="estado">
                    <Select
                        placeholder="Estado"
                        allowClear
                        style={{ width: 130 }}
                        onChange={() => form.submit()}
                        options={[
                            { value: 1, label: 'Habilitado' },
                            { value: 2, label: 'Deshabilitado' },
                        ]}
                    />
                </Form.Item>
                <Form.Item name="categoria_id">
                    <Select
                        placeholder="Categoría"
                        allowClear
                        showSearch
                        style={{ width: 200 }}
                        onChange={() => form.submit()}
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={categorias.map(c => ({
                            value: c.id,
                            label: `${'·'.repeat(Math.max(0, c.nivel - 1))} ${c.nombre}`.trim(),
                        }))}
                    />
                </Form.Item>
            </Form>

            {/* Tabla */}
            <Table
                dataSource={productos}
                columns={columns}
                rowKey="sku"
                loading={loading}
                size="small"
                bordered
                pagination={{
                    ...pagination,
                    showSizeChanger: false,
                    showTotal: (total) => `${total} productos`,
                    position: ['bottomRight'],
                }}
                onChange={(p) => fetchProductos(pais, p.current, filters)}
                locale={{ emptyText: <Empty description="Sin productos importados. Usa 'Importar productos' para comenzar." /> }}
            />

            {/* Drawer de detalle */}
            <ProductoDrawer
                producto={drawerProducto}
                visible={!!drawerProducto}
                onClose={() => setDrawerProducto(null)}
            />
        </div>
    )
}

export default MagentoIndex
